import 'react-block-ui/style.css';

import * as React from 'react';
import moment from 'moment-timezone';

import { DefaultButton, DialogFooter, TextField, PrimaryButton, Dropdown, IDropdownOption, CommandBarButton } from '@fluentui/react';

import BlockUi from 'react-block-ui';
import { PatientFileData } from '../../redux/patient-files/types';
import { DOCUMENT_TYPE } from '../../config/constants';
import { Visit } from '../../redux/visits/types';
import { AsyncData } from '../../redux/core';
import { VisitStatus } from '../../types';

export interface PatientFileDetailsProps {
  file: PatientFileData;
  patientId: string;
  visits?: AsyncData<Visit[]>;
  hideDocumentType?: boolean;
  patientFileAction?: (actionName: 'Sending' | 'Deleting' | 'Updating' | 'Releasing', file: PatientFileData, description?: string, visitCode?: string) => void;
  handleKeyPress?: (key: string) => void;
  isSaving?: boolean;
  onClosed?: (success: boolean) => void;
}

export interface PatientFileDetailsState {
  description: string;
  selectedVisit: string;
}

export class PatientFileDetails extends React.Component<PatientFileDetailsProps, PatientFileDetailsState> {
  constructor(props: PatientFileDetailsProps) {
    super(props);
    this.state = {
      description: this.props.file.description || '',
      selectedVisit: this.props.file.visit_code || '',
    };
  }

  public componentDidMount() {
    document.addEventListener('keydown', this.handleKeyPress, false);
  }

  public componentWillUnmount() {
    document.removeEventListener('keydown', this.handleKeyPress, false);
  }

  public componentDidUpdate(prevProps: PatientFileDetailsProps) {
    if (prevProps.isSaving && !this.props.isSaving) {
      this.close();
    }
    if (this.props.file !== prevProps.file) {
      this.setState({
        description: this.props.file.description || '',
        selectedVisit: this.props.file.visit_code || '',
      });
    }
  }

  public render(): JSX.Element {
    const { file, isSaving, hideDocumentType } = this.props;

    return (
      <BlockUi tag="div" blocking={isSaving}>
        <div className="flex flex-row">
          <div style={{ flex: 1, width: 0 }}>
            <p style={{ fontSize: 20, marginTop: 0 }}>File details{file.visit_code ? ' for visit: ' + file.visit_code : ': '}</p>
          </div>
          <div style={{ flex: 1, width: 0, textAlign: 'right' }}>
            <CommandBarButton
              disabled={file.download_url === null}
              href={file.download_url}
              style={{ padding: 8 }}
              iconProps={{ iconName: 'OpenInNewWindow' }}
              target="_blank"
              text="Open in new tab"
            />
          </div>
        </div>
        {file.extension === 'pdf' ? (
          <iframe title="document" style={{ width: 550, height: 600 }} src={file.download_url} />
        ) : (
          <img src={file.download_url} style={{ maxWidth: '100%' }} alt="" />
        )}
        {file.document_type && !hideDocumentType && (
          <div style={{ marginTop: 10 }}>
            <TextField label="Type" value={this.formatDocumentType(file.document_type)} borderless={true} readOnly={true} />
          </div>
        )}
        <div style={{ marginTop: 10 }}>
          <TextField
            label="Description"
            placeholder="Enter a file description"
            value={this.state.description}
            onChange={(ev, value) => this.setState({ description: value || '' })}
            borderless={!!file.approved_on}
            readOnly={!!file.approved_on}
          />
        </div>
        {file.approved_on && file.visit_code && (
          <div style={{ marginTop: 10 }}>
            <TextField label="Visit Code" value={file.visit_code} borderless={true} readOnly={true} />
          </div>
        )}
        {this.props.patientFileAction && !file.approved_on && this.props.visits && (
          <div style={{ marginTop: 10 }}>
            <Dropdown
              label="Visit Code"
              disabled={this.props.visits.isFetching}
              placeholder="Select a Visit Code"
              selectedKey={this.state.selectedVisit}
              options={this.listVisits()}
              onChange={(e, o) => o && this.setState({ selectedVisit: String(o.key) })}
            />
          </div>
        )}
        {file._created && (
          <div style={{ marginTop: 10 }}>
            <TextField
              label="Uploaded"
              value={
                moment(file._created).tz(moment.tz.guess()).format('MM/DD/YY hh:mma z')
                // `${file.uploaded_by ? ' by ' + file.uploaded_by : ''}`
              }
              borderless={true}
              readOnly={true}
            />
          </div>
        )}

        {file.released_at && (
          <div style={{ marginTop: 10 }}>
            <TextField
              label="Sent to Patient"
              value={
                moment.utc(file.released_at).tz(moment.tz.guess()).format('MM/DD/YY hh:mma z')
                // `${file.released_by ? ' by ' + file.released_by : ''}`
              }
              borderless={true}
              readOnly={true}
            />
          </div>
        )}

        {this.props.patientFileAction && (
          <div style={{ marginTop: 40 }}>
            <DialogFooter>
              <PrimaryButton disabled={!!file.approved_on} onClick={this.onUpdatePatientFile} iconProps={{ iconName: 'Refresh' }} text="Update" />
              <DefaultButton disabled={file.context !== 'unreleased'} onClick={this.onReleasePatientFile} iconProps={{ iconName: 'Send' }} text="Send to Patient" />
              <br />
              <CommandBarButton style={{ marginLeft: 10, marginTop: 30, padding: 8 }} onClick={this.onDeletePatientFile} iconProps={{ iconName: 'Delete' }} text="Delete file" />
            </DialogFooter>
          </div>
        )}
      </BlockUi>
    );
  }

  private listVisits(): IDropdownOption[] {
    const { visits } = this.props;
    let visitList: IDropdownOption[] = [];

    if (visits && !visits.isFetching && visits.data) {
      visitList = visits.data
        .filter((v: Visit) => v.status !== VisitStatus.CANCELLED)
        .map((v) => ({
          key: v.visitCode,
          text: `${v.visitCode} - ${v.longDateString} - ${v.visitServiceName}`,
        }));
    }

    return visitList;
  }

  private formatDocumentType(type: string): string {
    const document_type = DOCUMENT_TYPE.find((t) => t.key === type);
    return document_type ? document_type.text : type;
  }

  private handleKeyPress = (e: KeyboardEvent) => {
    if (this.props.handleKeyPress) {
      this.props.handleKeyPress(e.key);
    }
  };

  private onDeletePatientFile = () => {
    if (this.props.patientFileAction) {
      this.props.patientFileAction('Deleting', this.props.file);
    }
  };

  private onReleasePatientFile = () => {
    if (this.props.patientFileAction) {
      this.props.patientFileAction('Releasing', this.props.file);
    }
  };

  private onUpdatePatientFile = () => {
    if (this.props.patientFileAction) {
      this.props.patientFileAction('Updating', this.props.file, this.state.description, this.state.selectedVisit);
    }
  };

  private onApprovePatientFile = () => {
    if (this.props.patientFileAction) {
      this.props.patientFileAction('Sending', this.props.file, this.state.description);
    }
  };

  private close = (success = false) => {
    if (this.props.onClosed) {
      this.props.onClosed(success);
    }
  };
}
